/**
 * Conversión entre porcentaje de salario y euros mensuales para aportaciones
 * de empresas con `employeeContributionType = PERCENTAGE`.
 *
 * El backend guarda y opera estas aportaciones en PORCENTAJE de punta a punta
 * (aportación mensual = grossAnnualSalary × pct / 100 / 12). El front solo debe
 * mostrar los euros equivalentes como referencia; el valor que se persiste es el %.
 *
 * - `percentageToMonthlyEur`: para mostrar los euros equivalentes a un % (display).
 * - `monthlyEurToPercentage`: SOLO para derivar el tope en % a partir de un cap en
 *   euros. NO usar para guardar el valor que elige el usuario (el slider ya opera
 *   en % y se guarda en su unidad nativa, evitando conversiones que siembran el bug).
 */

export const percentageToMonthlyEur = (pct: number, grossAnnualSalary: number): number =>
	!grossAnnualSalary || pct <= 0 ? 0 : (grossAnnualSalary * pct) / 100 / 12;

export const monthlyEurToPercentage = (eur: number, grossAnnualSalary: number): number =>
	!grossAnnualSalary || eur <= 0 ? 0 : (eur * 12 * 100) / grossAnnualSalary;

/**
 * Rejilla del porcentaje de aportación.
 *
 * El slider avanza de 0,05 en 0,05, así que TODO porcentaje que se persista debe ser
 * múltiplo de 0,05 (y por tanto caber en 2 decimales). No es un capricho de formato:
 * toda la UI pinta el % con 2 decimales, de modo que un valor con más decimales es
 * imposible de mostrar con honestidad — el % que se enseña y el euro que se cobra
 * dejan de corresponderse, y cada capa recorta por un sitio distinto.
 *
 * El caso real que lo destapó: al arrastrar el slider hasta el tope se guardaba el
 * tope CRUDO (`6,635294117647059 %`, `1,505 %`), que es un cálculo, no una elección
 * del usuario. La pantalla mostraba entonces dos euros distintos para la misma
 * aportación.
 *
 * - `snapPercentageToGrid`: al múltiplo más cercano. Para el valor que ELIGE el usuario.
 * - `floorPercentageToGrid`: hacia abajo. Para TOPES — quedarse unos céntimos por
 *   debajo del máximo real es correcto; pasarse, nunca.
 * - `ceilPercentageToGrid`: hacia arriba. Para MÍNIMOS — redondear a la baja dejaría al
 *   partícipe por debajo del mínimo pactado con su empresa.
 */
export const CONTRIBUTION_PERCENTAGE_STEP = 0.05;

/** Tolerancia para el ruido binario: 1,9 / 0,05 da 37,999…9, no 38. */
const GRID_EPSILON = 1e-9;

const toSteps = (pct: number): number => pct / CONTRIBUTION_PERCENTAGE_STEP;

/** Vuelve de escalones a porcentaje limpiando el ruido de la coma flotante. */
const fromSteps = (steps: number): number =>
	Number((steps * CONTRIBUTION_PERCENTAGE_STEP).toFixed(2));

/**
 * Aplica `round` a los escalones, pero solo cuando el valor NO está ya en la rejilla:
 * si lo está (salvo ruido binario), redondear arriba o abajo lo movería un escalón entero.
 */
const alignToGrid = (pct: number, round: (steps: number) => number): number => {
	// Number(): estos valores llegan del backend como STRING (columnas `numeric` de
	// Postgres). Sin coacción, `Number.isFinite('0.5')` es false y devolveríamos 0,
	// borrando el mínimo de la empresa en silencio.
	const value = Number(pct);
	if (!Number.isFinite(value) || value <= 0) return 0;
	const steps = toSteps(value);
	const nearest = Math.round(steps);
	return fromSteps(Math.abs(steps - nearest) < GRID_EPSILON ? nearest : round(steps));
};

export const snapPercentageToGrid = (pct: number): number => alignToGrid(pct, Math.round);

export const floorPercentageToGrid = (pct: number): number => alignToGrid(pct, Math.floor);

export const ceilPercentageToGrid = (pct: number): number => alignToGrid(pct, Math.ceil);
